import type { MonospacePage } from '#layers/data-model/public';
import { defineStore } from 'pinia';
import { computed } from 'vue';
import { defineWorkspaceEntityHydration, useWorkspaceEntityStore } from '#layers/runtime/public';

/**
 * Every `MonospacePage` row of a workspace runtime.
 *
 * Declared as a hydration rather than loaded with a one-shot `findMany` because the rows have to
 * survive the components that render them. RStore garbage-collects a cached row once no live query
 * references it, so closing the page builder used to evict a customized content page; a cache-only
 * lookup then reported "not customized" and the next create violated the `override_page` unique
 * constraint. A hydration query lives in the runtime's scope, so the rows live as long as the
 * workspace does. It replaces the `findMany` that `prepareWorkspaceSpaceRoutes` used to issue, so
 * the switch still loads pages exactly once.
 *
 * Scope is every page, not just content overrides: custom-space pages are read through `peekFirst`
 * in `usePageIntent`, `useCurrentPageNotRendered`, `SpaceBreadcrumb` and `PageSelector` and had the
 * same latent gap, which retention alone closes.
 */
export const workspacePagesHydration = defineWorkspaceEntityHydration((store) => store.MonospacePage.query((q) => q.many({
	// Limit the number of pages fetched to avoid overwhelming the client with too many items.
	// TODO Handle more than 1000 pages for a single workspace
	limit: 1000,
})), { empty: [] });

/**
 * Authoritative existence check for the page that replaces `overridePageId`.
 *
 * `fetch-only` because `override_page` is unique in the database: the cache can be empty for a row
 * that exists, and a create guarded on that answer duplicates it. Callers derive the id themselves,
 * so this stays free of content-page semantics.
 */
async function fetchOverridePage(overridePageId: string): Promise<MonospacePage | undefined> {
	// Resolved per call: the Pinia store outlives workspace runtimes, and a retired runtime's transport
	// is aborted, so an entity store captured at setup would fail every request after a switch.
	const store = useWorkspaceEntityStore();

	const found = await store.MonospacePage.findFirst({
		filter: { overridePage: { _eq: overridePageId } },
		fetchPolicy: 'fetch-only',
	});

	return found?.overridePage === overridePageId ? found : undefined;
}

export const useWorkspacePagesStore = defineStore('workspace-pages', () => {
	const { data: pages, refresh: refreshPages } = workspacePagesHydration.use();

	/** The pages that stand in for a generated page. */
	const overridePages = computed(() => pages.value.filter((page) => page.overridePage !== null));

	return {
		pages,
		overridePages,
		refreshPages,
		fetchOverridePage,
	};
});
